import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  username: string
  domain?: string
  display_name?: string
  role: string
  authMethod?: string
  ssoProviderId?: string
}

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  hydrated: boolean
  // 2FA state
  requires2FA: boolean
  requiresEnrollment: boolean
  partialToken: string | null
  login: (token: string) => void
  login2FA: (fullToken: string) => void
  logout: () => void
  setRequires2FA: (token: string) => void
  setRequiresEnrollment: (token: string) => void
  clear2FAState: () => void
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

function isTokenValid(token: string | null): boolean {
  if (!token) return false
  const payload = decodeJwtPayload(token)
  if (!payload?.exp) return false
  return payload.exp * 1000 > Date.now()
}

function userFromToken(token: string): User {
  const payload = decodeJwtPayload(token)
  return {
    username: payload?.sub || '',
    domain: payload?.domain,
    display_name: payload?.display_name,
    role: payload?.role || 'user',
    authMethod: payload?.auth_method || 'local',
    ssoProviderId: payload?.sso_provider_id,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      hydrated: false,
      requires2FA: false,
      requiresEnrollment: false,
      partialToken: null,

      login: (token) => {
        set({
          token,
          user: userFromToken(token),
          isAuthenticated: true,
          requires2FA: false,
          requiresEnrollment: false,
          partialToken: null,
        })
      },

      login2FA: (fullToken) => {
        set({
          token: fullToken,
          user: userFromToken(fullToken),
          isAuthenticated: true,
          requires2FA: false,
          requiresEnrollment: false,
          partialToken: null,
        })
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          requires2FA: false,
          requiresEnrollment: false,
          partialToken: null,
        })
      },

      setRequires2FA: (token) => {
        set({
          partialToken: token,
          requires2FA: true,
          requiresEnrollment: false,
          isAuthenticated: false,
        })
      },

      setRequiresEnrollment: (token) => {
        set({
          partialToken: token,
          requires2FA: false,
          requiresEnrollment: true,
          isAuthenticated: false,
        })
      },

      clear2FAState: () => {
        set({
          requires2FA: false,
          requiresEnrollment: false,
          partialToken: null,
        })
      },
    }),
    {
      name: 'flexilabs_auth',
      partialize: (state) => ({ token: state.token }),
      // After rehydration, validate the token and set state accordingly
      onRehydrateStorage: () => (state) => {
        if (!state) return
        if (state.token && isTokenValid(state.token)) {
          const payload = decodeJwtPayload(state.token)
          // If token has two_factor_authenticated: false, don't auto-login
          if (payload?.two_factor_authenticated === false) {
            state.token = null
            state.user = null
            state.isAuthenticated = false
          } else {
            state.isAuthenticated = true
            state.user = userFromToken(state.token)
          }
        } else if (state.token) {
          state.token = null
          state.user = null
          state.isAuthenticated = false
        }
        state.hydrated = true
      },
    }
  )
)